import type { ClientAction, PlayerView } from '@sh/shared'
import { ConfirmHint, HoldToConfirm } from '../../components/Confirm.js'
import { PolicyChoice } from '../../components/Hand.js'
import { usePendingCard } from '../../components/Nudge.js'
import { WaitingOn } from '../../components/Waiting.js'

export function PresidentPhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  const hand = view.presidentHand
  // Nobody may say a word to the President here - that is the rule - so a
  // table stalled on this card has nothing to do but wait for it.
  const pending = usePendingCard(true)

  if (!hand) {
    return (
      <section className="card">
        <h2 className="card__title">Legislative session</h2>
        <WaitingOn who={name(view.presidentId ?? '')}>
          <strong>{name(view.presidentId ?? '')}</strong> drew three and is choosing one to discard
        </WaitingOn>
        <p className="card__hint">No talking to the President about it. That is the rule.</p>
      </section>
    )
  }

  return (
    <section ref={pending.ref} className={`card ${pending.className}`}>
      <h2 className="card__title">Discard one policy</h2>
      <p className="card__hint">
        You drew three. Discard one face down and pass the other two to the Chancellor.
      </p>
      <PolicyChoice
        policies={hand}
        action="discard"
        onChoose={(i) => act({ type: 'president_discard', index: i })}
        testId="president-hand"
      />
      <p className="card__hint">
        The card you press is the one discarded. <ConfirmHint subject="card" />
      </p>
    </section>
  )
}

export function ChancellorPhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  const hand = view.chancellorHand
  const pending = usePendingCard(true)

  if (!hand) {
    return (
      <section className="card">
        <h2 className="card__title">Legislative session</h2>
        <WaitingOn who={name(view.chancellorId ?? '')}>
          <strong>{name(view.chancellorId ?? '')}</strong> is choosing which policy to enact
        </WaitingOn>
      </section>
    )
  }

  return (
    <section ref={pending.ref} className={`card ${pending.className}`}>
      <h2 className="card__title">Enact a policy</h2>
      <p className="card__hint">
        The President passed you two. Enact one; the other is discarded face down.
      </p>
      <PolicyChoice
        policies={hand}
        action="enact"
        onChoose={(i) => act({ type: 'chancellor_enact', index: i })}
        testId="chancellor-hand"
      />
      <p className="card__hint">
        The card you press is the one enacted. <ConfirmHint subject="card" />
      </p>
      {view.vetoUnlocked && (
        <HoldToConfirm
          className="btn--ghost btn--block"
          onConfirm={() => act({ type: 'request_veto' })}
          testId="request-veto"
        >
          I wish to veto this agenda
        </HoldToConfirm>
      )}
    </section>
  )
}

export function VetoPhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  const isPresident = view.you?.id === view.presidentId
  const pending = usePendingCard(true)

  if (!isPresident) {
    return (
      <section className="card">
        <h2 className="card__title">Veto proposed</h2>
        <WaitingOn who={name(view.presidentId ?? '')}>
          <strong>{name(view.chancellorId ?? '')}</strong> wishes to veto —{' '}
          {name(view.presidentId ?? '')} must answer
        </WaitingOn>
      </section>
    )
  }

  return (
    <section ref={pending.ref} className={`card ${pending.className}`}>
      <h2 className="card__title">The Chancellor wishes to veto</h2>
      <p className="card__hint">
        Agree and both policies are discarded — but that is an inactive government, and the election
        tracker advances. Refuse and the Chancellor must enact one.
      </p>
      <div className="choices choices--row">
        <HoldToConfirm
          className="btn--danger"
          onConfirm={() => act({ type: 'respond_veto', consent: true })}
          testId="veto-accept"
        >
          I agree to the veto
        </HoldToConfirm>
        <HoldToConfirm
          onConfirm={() => act({ type: 'respond_veto', consent: false })}
          testId="veto-refuse"
        >
          I refuse
        </HoldToConfirm>
      </div>
      <p className="card__hint"><ConfirmHint subject="button" /></p>
    </section>
  )
}
